import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { ShoppingCart, Zap, BarChart3, Users, CheckCircle2, ArrowRight, Package, TrendingUp, Shield, Globe, Link2, Store, Briefcase, Handshake, FileText, UserCheck, Headphones, Sparkles } from 'lucide-react'
import '@/lib/i18n'
import Container from '@/components/ui/Container'
import Section from '@/components/ui/Section'
import { useSeo } from '@/lib/seo'

export default function BassiliTradePage() {
  const { t } = useTranslation(['translation', 'home', 'bassilitrade'])

  useSeo({
    title: 'BassiliTrade — B2B eCommerce Integration Platform | Bassili Group',
    description:
      'BassiliTrade connects sellers and buyers across Amazon, eBay, Shopify and more with a unified dashboard, automated inventory management and real-time order synchronization.',
    path: '/bassilitrade',
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const features = [
    {
      icon: <Link2 className="w-6 h-6" />,
      titleKey: 'bassilitrade:features.multiMarketplace.title',
      descriptionKey: 'bassilitrade:features.multiMarketplace.description',
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      titleKey: 'bassilitrade:features.b2b.title',
      descriptionKey: 'bassilitrade:features.b2b.description',
    },
    {
      icon: <Handshake className="w-6 h-6" />,
      titleKey: 'bassilitrade:features.barter.title',
      descriptionKey: 'bassilitrade:features.barter.description',
    },
    {
      icon: <FileText className="w-6 h-6" />,
      titleKey: 'bassilitrade:features.tenders.title',
      descriptionKey: 'bassilitrade:features.tenders.description',
    },
    {
      icon: <UserCheck className="w-6 h-6" />,
      titleKey: 'bassilitrade:features.freelance.title',
      descriptionKey: 'bassilitrade:features.freelance.description',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      titleKey: 'bassilitrade:features.security.title',
      descriptionKey: 'bassilitrade:features.security.description',
    },
    {
      icon: <Globe className="w-6 h-6" />,
      titleKey: 'bassilitrade:features.global.title',
      descriptionKey: 'bassilitrade:features.global.description',
    },
    {
      icon: <Headphones className="w-6 h-6" />,
      titleKey: 'bassilitrade:features.support.title',
      descriptionKey: 'bassilitrade:features.support.description',
    },
  ]

  const benefits = [
    {
      icon: <Zap className="w-5 h-5" />,
      textKey: 'bassilitrade:benefits.saveTime',
    },
    {
      icon: <CheckCircle2 className="w-5 h-5" />,
      textKey: 'bassilitrade:benefits.reduceErrors',
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      textKey: 'bassilitrade:benefits.scale',
    },
    {
      icon: <Users className="w-5 h-5" />,
      textKey: 'bassilitrade:benefits.reach',
    },
  ]

  const marketplaces = [
    { name: 'Amazon', color: 'from-orange-500 to-yellow-500' },
    { name: 'eBay', color: 'from-blue-500 to-cyan-500' },
    { name: 'Shopify', color: 'from-green-500 to-emerald-500' },
    { name: 'Walmart', color: 'from-blue-600 to-indigo-600' },
    { name: 'Etsy', color: 'from-orange-400 to-red-500' },
    { name: 'Faire', color: 'from-teal-500 to-cyan-500' },
    { name: 'Rakuten', color: 'from-red-500 to-pink-500' },
    { name: 'Temu', color: 'from-orange-500 to-red-600' },
  ]

  return (
    <div className="relative">
      {/* Hero Section - Modern Launch Design */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-green-50 to-teal-50 dark:from-slate-950 dark:via-green-950 dark:to-teal-950 pt-20">
        {/* Animated Background Grid */}
        <div className="absolute inset-0 opacity-20 dark:opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, #2E8B57 1px, transparent 1px), linear-gradient(to bottom, #2E8B57 1px, transparent 1px)`,
              backgroundSize: '4rem 4rem',
            }}
          />
        </div>

        {/* Floating Gradient Orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-20 left-10 w-72 h-72 rounded-full bg-gradient-to-br from-green-400/30 to-teal-400/30 dark:from-green-500/20 dark:to-teal-500/20 blur-3xl"
            animate={{
              scale: [1, 1.3, 1],
              x: [0, 30, 0],
              y: [0, 20, 0],
            }}
            transition={{
              duration: 18,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-gradient-to-br from-emerald-400/30 to-cyan-400/30 dark:from-emerald-500/20 dark:to-cyan-500/20 blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              x: [0, -40, 0],
              y: [0, -25, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>

        <Container className="relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Top Content Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16 max-w-4xl mx-auto"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-green-500/10 to-teal-500/10 dark:from-green-500/20 dark:to-teal-500/20 border border-green-500/20 dark:border-green-500/30 mb-8 backdrop-blur-sm"
              >
                <ShoppingCart className="w-5 h-5 text-green-600 dark:text-green-400" />
                <span className="text-sm font-semibold text-green-700 dark:text-green-300">
                  {t('bassilitrade:hero.badge')}
                </span>
              </motion.div>

              {/* Main Heading */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="space-y-6 mb-8"
              >
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.1]">
                  <span className="bg-gradient-to-r from-green-600 via-teal-600 to-emerald-600 dark:from-green-400 dark:via-teal-400 dark:to-emerald-400 bg-clip-text text-transparent">
                    {t('bassilitrade:hero.title')}
                  </span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto">
                  {t('bassilitrade:hero.subtitle')}
                </p>
                <p className="text-lg text-gray-600 dark:text-gray-400 font-medium">
                  {t('bassilitrade:hero.description')}
                </p>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6"
              >
                <motion.a
                  href="https://www.bassilitrade.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-gradient-to-r from-green-500 via-green-600 to-teal-600 text-white rounded-2xl font-bold text-lg shadow-xl shadow-green-500/30 hover:shadow-2xl hover:shadow-green-500/50 transition-all duration-300"
                >
                  {t('bassilitrade:hero.cta.primary')}
                  <ArrowRight className="w-6 h-6" />
                </motion.a>
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-2xl font-bold text-lg border-2 border-gray-300 dark:border-slate-600 hover:border-green-500 dark:hover:border-green-500 transition-all duration-300 shadow-lg"
                  onClick={() => {
                    const element = document.querySelector('#features')
                    element?.scrollIntoView({ behavior: 'smooth' })
                  }}
                >
                  {t('bassilitrade:hero.cta.secondary')}
                </motion.button>
              </motion.div>

              {/* Trial Badge */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                {t('bassilitrade:hero.trial')}
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="flex flex-wrap gap-8 justify-center mt-12"
              >
                <div className="text-center">
                  <div className="text-4xl font-black text-green-600 dark:text-green-400">
                    {t('bassilitrade:stats.marketplaces')}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    {t('bassilitrade:stats.marketplacesLabel')}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-green-600 dark:text-green-400">
                    {t('bassilitrade:stats.countries')}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    {t('bassilitrade:stats.countriesLabel')}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-green-600 dark:text-green-400">
                    {t('bassilitrade:stats.support')}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    {t('bassilitrade:stats.supportLabel')}
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Marketplace Logos Preview */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="flex flex-wrap gap-4 justify-center items-center max-w-4xl mx-auto"
            >
              {marketplaces.map((marketplace, index) => (
                <motion.div
                  key={marketplace.name}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 1 + index * 0.05 }}
                  className={`px-4 py-2 rounded-full bg-gradient-to-r ${marketplace.color} text-white text-sm font-semibold shadow-lg`}
                >
                  {marketplace.name}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <Section id="features" className="bg-white dark:bg-slate-900">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('bassilitrade:features.title')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('bassilitrade:features.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-50 to-white dark:from-slate-800 dark:to-slate-900 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-slate-700 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl text-white mb-4 bg-gradient-to-r from-green-500 to-teal-600">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">
                  {t(feature.titleKey)}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  {t(feature.descriptionKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Marketplaces Section */}
      <Section className="bg-gradient-to-br from-green-50 via-teal-50 to-emerald-50 dark:from-slate-800 dark:via-slate-900 dark:to-green-950">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('bassilitrade:marketplaces.title')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {t('bassilitrade:marketplaces.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 max-w-6xl mx-auto">
            {marketplaces.map((marketplace, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className={`bg-gradient-to-br ${marketplace.color} rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105`}
              >
                <Store className="w-8 h-8 text-white mx-auto mb-2" />
                <h3 className="text-sm font-bold text-white text-center">
                  {marketplace.name}
                </h3>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Benefits Section */}
      <Section className="bg-white dark:bg-slate-900">
        <Container>
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
                {t('bassilitrade:benefits.title')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {t('bassilitrade:benefits.subtitle')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-center gap-4 bg-gradient-to-br from-gray-50 to-white dark:from-slate-800 dark:to-slate-900 rounded-lg p-4 shadow-md border border-gray-100 dark:border-slate-700"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg text-white flex items-center justify-center bg-gradient-to-r from-green-500 to-teal-600">
                    {benefit.icon}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 font-medium">
                    {t(benefit.textKey)}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Use Cases Section */}
      <Section className="bg-gradient-to-br from-green-50 via-teal-50 to-emerald-50 dark:from-slate-800 dark:via-slate-900 dark:to-green-950">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              {t('bassilitrade:useCases.title')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('bassilitrade:useCases.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                titleKey: 'bassilitrade:useCases.sellers.title',
                descriptionKey: 'bassilitrade:useCases.sellers.description',
                icon: <ShoppingCart className="w-8 h-8" />,
                gradient: 'from-green-500 to-teal-500',
              },
              {
                titleKey: 'bassilitrade:useCases.b2b.title',
                descriptionKey: 'bassilitrade:useCases.b2b.description',
                icon: <Briefcase className="w-8 h-8" />,
                gradient: 'from-teal-500 to-cyan-500',
              },
              {
                titleKey: 'bassilitrade:useCases.freelancers.title',
                descriptionKey: 'bassilitrade:useCases.freelancers.description',
                icon: <UserCheck className="w-8 h-8" />,
                gradient: 'from-emerald-500 to-green-500',
              },
            ].map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-slate-700 hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${useCase.gradient} mb-4 flex items-center justify-center text-white`}>
                  {useCase.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
                  {t(useCase.titleKey)}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t(useCase.descriptionKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* CTA Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-green-600 via-teal-600 to-emerald-600">
        <Section className="relative">
          <div className="absolute inset-0 opacity-20">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                backgroundSize: '4rem 4rem',
              }}
            />
          </div>

          <Container className="relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                {t('bassilitrade:cta.title')}
              </h2>
              <p className="text-xl text-white/90 mb-8">
                {t('bassilitrade:cta.subtitle')}
              </p>
              <a
                href="https://www.bassilitrade.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-green-600 rounded-xl font-semibold text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                {t('bassilitrade:cta.button')}
                <ArrowRight className="w-5 h-5" />
              </a>
            </motion.div>
          </Container>
        </Section>
      </div>
    </div>
  )
}
